import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../utils/apiClient';
import config from '../utils/config';

export interface Payment {
  ID: number;
  CreatedAt: string;
  UpdatedAt: string;
  order_id: string;
  customer_id: number;
  appointment_id: number;
  amount: number;
  discount: number;
  tax: number;
  net_amount: number;
  currency: string;
  status: string;
  payment_method: string;
  gateway_name: string;
  gateway_order_id: string;
  gateway_payment_id: string;
  description: string;
  coupon_code: string;
  refunded_amount: number;
  paid_at: string;
  failed_at: string;
  failure_reason: string;
  refunds: any[];
}

export interface RevenueData {
  total_revenue: number;
  total_payments: number;
  successful_payments: number;
  failed_payments: number;
  refunded_amount: number;
}

interface PaymentsState {
  payments: Payment[];
  selectedPayment: Payment | null;
  revenue: RevenueData | null;
  loading: boolean;
  error: string | null;
}

const initialState: PaymentsState = {
  payments: [],
  selectedPayment: null,
  revenue: null,
  loading: false,
  error: null,
};

export const fetchPayments = createAsyncThunk(
  'payments/fetchAll',
  async (_, thunkAPI) => {
    try {
      const response = await apiClient.get(`${config.services.pyms}/api/v1/payments`);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to fetch payments');
    }
  }
);

export const fetchPaymentById = createAsyncThunk(
  'payments/fetchById',
  async (id: number, thunkAPI) => {
    try {
      const response = await apiClient.get(`${config.services.pyms}/api/v1/payments/${id}`);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to fetch payment');
    }
  }
);

export const initiateRefund = createAsyncThunk(
  'payments/refund',
  async ({ id, reason, amount }: { id: number; reason: string; amount?: number }, thunkAPI) => {
    try {
      const response = await apiClient.post(`${config.services.pyms}/api/v1/payments/${id}/refund`, { reason, amount });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to initiate refund');
    }
  }
);

export const markPaymentFailed = createAsyncThunk(
  'payments/markFailed',
  async ({ id, reason }: { id: number; reason: string }, thunkAPI) => {
    try {
      const response = await apiClient.post(`${config.services.pyms}/api/v1/payments/${id}/fail`, { failure_reason: reason });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to mark payment as failed');
    }
  }
);

export const fetchRevenue = createAsyncThunk(
  'payments/fetchRevenue',
  async (_, thunkAPI) => {
    try {
      const today = new Date();
      const startDate = `${today.getFullYear()}-01-01`;
      const endDate = today.toISOString().split('T')[0];
      const response = await apiClient.get(`${config.services.pyms}/api/v1/payments/analytics/revenue`, {
        params: { start_date: startDate, end_date: endDate },
      });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || 'Failed to fetch revenue');
    }
  }
);

const paymentsSlice = createSlice({
  name: 'payments',
  initialState,
  reducers: {
    clearSelectedPayment: (state) => { state.selectedPayment = null; },
    clearPaymentsError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPayments.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchPayments.fulfilled, (state, action) => { state.loading = false; state.payments = Array.isArray(action.payload?.data) ? action.payload.data : Array.isArray(action.payload) ? action.payload : []; })
      .addCase(fetchPayments.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })
      .addCase(fetchPaymentById.fulfilled, (state, action) => { state.selectedPayment = action.payload?.data || action.payload; })
      .addCase(initiateRefund.fulfilled, (state, action) => {
        const updated = action.payload?.data || action.payload;
        if (updated) {
          const idx = state.payments.findIndex(p => p.ID === updated.ID);
          if (idx !== -1) state.payments[idx] = updated;
        }
      })
      .addCase(fetchRevenue.fulfilled, (state, action) => { state.revenue = action.payload?.data?.revenue || action.payload?.data || action.payload; })
      .addCase(fetchRevenue.rejected, (state, action) => { state.error = action.payload as string; });
  },
});

export const { clearSelectedPayment, clearPaymentsError } = paymentsSlice.actions;
export default paymentsSlice.reducer;
